import React, { useCallback, useMemo } from 'react';
import Flag from 'react-world-flags';
import classnames from 'classnames';
import {
  IPerformerEntry,
  ITableConfig,
  TopPerformersSortBy,
  TopPerformersSortDirection,
} from 'services/DashboardApi/types';
import { formatSalesValue, formatBookingCount } from './formatters';
import { SortIcon } from 'ui/Table/SortIcon';
import { ESortOrder } from 'store/common/types';
import StarMedalIcon from 'ui/Icons/star-medal.component.svg';
import ChevronDownIcon from 'ui/Icons/tabler/chevron-down.component.svg';
import ChevronUpIcon from 'ui/Icons/tabler/chevron-up.component.svg';
import { getCountryName } from 'utils/country';
import { TCountryCode } from 'interfaces';
import { HidingTextTooltip } from 'ui/Tooltip/HidingTextTooltip';

const EXPANDED_LIMIT = 10;

interface ITopPerformersTableProps {
  title: string;
  data: IPerformerEntry[];
  totalCount: number;
  config: ITableConfig;
  onSortChange: (sortBy: TopPerformersSortBy, sortDirection: TopPerformersSortDirection) => void;
  onToggleExpand: () => void;
  isUpdating?: boolean;
}

export const TopPerformersTable: React.FC<ITopPerformersTableProps> = ({
  title,
  data,
  totalCount,
  config,
  onSortChange,
  onToggleExpand,
  isUpdating = false,
}) => {
  const { sortBy, sortDirection, limit } = config;
  const isExpanded = limit === EXPANDED_LIMIT;
  const showExpandLink = totalCount > 5 || isExpanded;

  // Convert our sort types to ESortOrder for SortIcon compatibility
  const sortOrder = sortDirection === 'asc' ? ESortOrder.ASC : ESortOrder.DESC;

  const handleHeaderClick = useCallback(
    (column: TopPerformersSortBy) => {
      if (column === sortBy) {
        // Toggle direction
        const newDirection = sortDirection === 'desc' ? 'asc' : 'desc';
        onSortChange(column, newDirection);
      } else {
        // New column, default to desc
        onSortChange(column, 'desc');
      }
    },
    [sortBy, sortDirection, onSortChange]
  );

  const headerCellClass = useCallback(
    (column: TopPerformersSortBy) => {
      const isActive = sortBy === column;
      return classnames('px-[5px] py-[2px] cursor-pointer select-none', isActive ? 'bg-gray-10 rounded-3px' : '');
    },
    [sortBy]
  );

  const visibleData = useMemo(() => {
    return data.slice(0, limit);
  }, [data, limit]);

  return (
    <div className={classnames('flex flex-col gap-[10px]', { 'opacity-50': isUpdating })}>
      <span className="font-pt-sans text-13px uppercase tracking-2xs text-black">{title}</span>

      <div className="flex flex-col">
        {/* Header Row */}
        <div className="flex items-center gap-[10px] px-[10px] py-[5px] bg-white-hard border border-gray-20 border-b-0">
          <div className="w-[35px] flex justify-center">
            <span className="font-pt-sans text-12px uppercase tracking-2xs text-gray-100">rank</span>
          </div>
          <div className="flex-1">
            <span className="font-pt-sans text-12px uppercase tracking-2xs text-gray-100">name</span>
          </div>
          <div
            className={classnames('w-[90px] pr-[8px]', headerCellClass('count'))}
            onClick={() => handleHeaderClick('count')}
          >
            <div className="flex items-center justify-end gap-[5px]">
              <span className="font-pt-sans text-12px uppercase tracking-2xs text-gray-100">bookings</span>
              <SortIcon sortBy={sortBy} sortOrder={sortOrder} newSortBy="count" />
            </div>
          </div>
          <div
            className={classnames('w-[80px] pr-[8px]', headerCellClass('value'))}
            onClick={() => handleHeaderClick('value')}
          >
            <div className="flex items-center justify-end gap-[5px]">
              <span className="font-pt-sans text-12px uppercase tracking-2xs text-gray-100">sales</span>
              <SortIcon sortBy={sortBy} sortOrder={sortOrder} newSortBy="value" />
            </div>
          </div>
        </div>

        {/* Data Rows */}
        <div className="flex flex-col">
          {visibleData.length === 0 ? (
            <div className="flex items-center justify-center px-[10px] py-[20px] bg-white border border-gray-20">
              <span className="font-pt-sans text-15px text-gray-100">No Results</span>
            </div>
          ) : (
            visibleData.map(performer => {
              const isWinner = performer.rank === 1;
              return (
                <div
                  key={performer.uuid}
                  className={classnames(
                    'flex items-center gap-[10px] px-[10px] py-[5px] bg-white',
                    isWinner ? 'border border-yellow-advert' : 'border-l border-r border-b border-gray-20'
                  )}
                >
                  {/* Rank */}
                  <div className="w-[35px] flex justify-center">
                    {isWinner ? (
                      <StarMedalIcon />
                    ) : (
                      <span className="font-pt-sans text-15px text-black">{performer.rank}</span>
                    )}
                  </div>

                  {/* Name with Flag */}
                  <div className="flex-1 flex items-start gap-[10px] min-w-0">
                    <HidingTextTooltip
                      tooltipContent={getCountryName(performer.countryCode as TCountryCode)}
                      position="bottom"
                      tooltipContentClassname="py-[3px] px-[6px]"
                      elementClassname="flex-shrink-0 mt-[2px]"
                    >
                      <Flag code={performer.countryCode} width={18} height={18} />
                    </HidingTextTooltip>
                    <span className="font-pt-sans text-15px text-black">{performer.name}</span>
                  </div>

                  {/* Bookings */}
                  <div className="w-[90px] pr-[8px] text-right">
                    <span className="font-pt-sans text-15px text-black">
                      {formatBookingCount(performer.bookingCount)}
                    </span>
                  </div>

                  {/* Sales */}
                  <div className="w-[80px] pr-[8px] text-right">
                    <span className="font-pt-sans text-15px text-black">
                      {formatSalesValue(performer.bookingValueCents)}
                    </span>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Show more/less link */}
      {showExpandLink && (
        <button
          type="button"
          onClick={onToggleExpand}
          className="flex items-center gap-[4px] font-pt-sans text-15px text-brown-prime hover:underline bg-transparent border-none cursor-pointer p-0"
        >
          <span>{isExpanded ? 'Show less' : 'Show more'}</span>
          {isExpanded ? (
            <ChevronUpIcon className="w-4 h-4 fill-transparent stroke-current stroke-2" />
          ) : (
            <ChevronDownIcon className="w-4 h-4 fill-transparent stroke-current stroke-2" />
          )}
        </button>
      )}
    </div>
  );
};
